/**
 * Sentry wiring. The project ships a Sentry DSN (REACT_APP_SENTRY_DSN) and
 * stamps a release (REACT_APP_SENTRY_RELEASE, set from the git SHA at build
 * time); `initSentry` turns that into a live client so front-end errors report
 * somewhere and the client-minted error id (see ErrorBoundary) is traceable.
 *
 * PRO-136: the SDK is loaded with a DYNAMIC import, off the boot path. A static
 * `import * as Sentry from '@sentry/react'` put @sentry/core + @sentry/browser
 * (~230 KB raw) into the entry chunk for what is a bare `Sentry.init({dsn})` —
 * no tracing, no replay. Instead `initSentry()` synchronously installs a tiny
 * `window` error/unhandledrejection buffer and schedules the real SDK for an
 * idle callback after first paint; everything caught in that window is queued
 * and flushed into the client the moment it is live. The buffering is the
 * load-bearing part: boot-time errors are exactly the ones worth reporting.
 *
 * `initSentry` is gated on the DSN: local dev and any build without one never
 * load the SDK and never send. The DSN is a public client-side identifier
 * (Sentry ingest key), not a secret — REACT_APP_-prefixed so it is deliberately
 * inlined into the bundle. No server-side token is referenced here.
 */

type SentryModule = typeof import('@sentry/react')

/**
 * Where a buffered capture came from. @sentry/browser's own global handlers
 * would have filed these as UNHANDLED; recording the source at enqueue time is
 * what lets the flush reproduce that mechanism instead of guessing later.
 */
type CaptureSource = 'onerror' | 'onunhandledrejection'

/** A capture that happened before the SDK finished loading. */
type QueuedCapture = {
  error: unknown
  componentStack?: string | null
  errorId?: string | null
  source?: CaptureSource
}

/**
 * Bounded on purpose: a boot-time error loop (a render crash retrying, a
 * rejected poll) must not grow this array without limit while the SDK loads.
 * The first N errors are the diagnostic ones anyway.
 */
const MAX_QUEUED = 30

let sentry: SentryModule | null = null
let loading: Promise<SentryModule | null> | null = null
/** The chunk is gone for the life of the tab: nothing can drain the queue. */
let loadFailed = false
let chunkInFlight = false
const queue: QueuedCapture[] = []
let bufferAttached = false

const getDsn = () => process.env.REACT_APP_SENTRY_DSN

function enqueue(item: QueuedCapture): void {
  if (loadFailed || queue.length >= MAX_QUEUED) return
  queue.push(item)
}

function capture(module: SentryModule, item: QueuedCapture): void {
  // A buffered global-handler error is one the SDK would itself have recorded
  // as unhandled; capturing it plainly would file it as `generic`/handled and
  // drop it out of every alert and search scoped to unhandled crashes.
  if (item.source) {
    module.captureException(item.error, {
      mechanism: { type: item.source, handled: false },
    })
    return
  }
  // Boundary captures are genuinely handled — React caught them — and keep the
  // default mechanism alongside their error id and componentStack.
  module.captureException(item.error, {
    tags: item.errorId ? { errorId: item.errorId } : undefined,
    contexts: item.componentStack
      ? { react: { componentStack: item.componentStack } }
      : undefined,
  })
}

function handleWindowError(event: ErrorEvent): void {
  enqueue({ error: event.error ?? event.message, source: 'onerror' })
}

function handleRejection(event: PromiseRejectionEvent): void {
  enqueue({ error: event.reason, source: 'onunhandledrejection' })
}

/**
 * Buffer window errors until the real client is live. Installed synchronously
 * by `initSentry` so there is no gap between boot and the SDK landing.
 */
function attachBuffer(): void {
  if (bufferAttached || typeof window === 'undefined') return
  bufferAttached = true
  window.addEventListener('error', handleWindowError)
  window.addEventListener('unhandledrejection', handleRejection)
}

/**
 * Detached the moment the SDK is live — @sentry/browser installs its own global
 * handlers, so keeping ours would double-report every subsequent error.
 */
function detachBuffer(): void {
  if (!bufferAttached || typeof window === 'undefined') return
  bufferAttached = false
  window.removeEventListener('error', handleWindowError)
  window.removeEventListener('unhandledrejection', handleRejection)
}

/**
 * Load + init the SDK exactly once, then flush everything buffered so far.
 * Resolves to null when there is no DSN (nothing is ever loaded or sent).
 */
function loadSentry(): Promise<SentryModule | null> {
  if (loading) return loading

  const dsn = getDsn()
  if (!dsn) {
    loading = Promise.resolve(null)
    return loading
  }

  chunkInFlight = true
  loading = import('@sentry/react')
    .then((module) => {
      module.init({
        dsn,
        environment: process.env.REACT_APP_ENVIRONMENT || undefined,
        release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
      })
      sentry = module
      detachBuffer()
      // Drain rather than iterate: anything captured while flushing goes
      // straight to the live client, not back onto the queue.
      const buffered = queue.splice(0, queue.length)
      buffered.forEach((item) => capture(module, item))
      return module
    })
    .catch(() => {
      // A blocked/failed SDK chunk must never take the app down with it. The
      // queue is released and stays closed: reporting is dead for this tab, so
      // retaining errors nothing can ever drain is a pure leak.
      loadFailed = true
      detachBuffer()
      queue.length = 0
      return null
    })
    .finally(() => {
      chunkInFlight = false
    })

  return loading
}

/**
 * Whether the SDK chunk fetch is in flight right now.
 *
 * Vite compiles the dynamic import above into `__vitePreload`, which dispatches
 * `vite:preloadError` on window when the fetch fails — and the entry's listener
 * answers that by hard-reloading the tab to recover from a stale lazy-route
 * chunk. This telemetry chunk is background, non-essential work that already
 * swallows its own failure, so the entry consults this to skip the reload for
 * it alone; every real route chunk keeps that recovery.
 */
export function isSentryChunkLoading(): boolean {
  return chunkInFlight
}

/** Run `fn` once the browser is idle after first paint (or ASAP as fallback). */
function whenIdle(fn: () => void): void {
  if (typeof window === 'undefined') {
    fn()
    return
  }
  const ric = (
    window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void
    }
  ).requestIdleCallback
  if (typeof ric === 'function') {
    ric(fn, { timeout: 5000 })
  } else {
    window.setTimeout(fn, 1000)
  }
}

/**
 * Install error reporting. Synchronous and cheap: it attaches the buffer and
 * schedules the SDK for after first paint. A no-op without a DSN.
 */
export function initSentry(): void {
  if (!getDsn()) return
  attachBuffer()
  // loadSentry never rejects (it swallows a failed chunk), so there is nothing
  // to attach a rejection handler to — the promise is deliberately dropped.
  whenIdle(() => {
    loadSentry()
  })
}

/**
 * Report an error caught by the app ErrorBoundary. Ties the report to the same
 * client-minted error id shown to the user and logged to the console, and
 * attaches the React componentStack, so a user's quote, the console line, and
 * the Sentry issue all reference one event. A no-op if no DSN is configured.
 *
 * Signature and fire-and-forget semantics are unchanged for callers. If the SDK
 * is still loading the capture is queued and flushed on arrival; a boundary
 * error also promotes the load from "when idle" to "now" — the app has already
 * crashed, so there is no paint left to protect.
 */
export function reportBoundaryError(
  error: unknown,
  componentStack: string | null | undefined,
  errorId: string | null
): void {
  if (sentry) {
    capture(sentry, { error, componentStack, errorId })
    return
  }
  // No DSN, or the SDK chunk already failed: there is no client to reach and
  // no flush left to queue for.
  if (loadFailed || !getDsn()) return
  enqueue({ error, componentStack, errorId })
  loadSentry()
}
